// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { useState } from "react";

import PropTypes from "prop-types";

import {
  SearchBarWithFilters,
  Button,
  ButtonsWrapper,
} from "components/elements";
import { filterSectionPropTypes } from "components/elements/SearchBarWithFilters/propTypes";
import { TableBody } from "components/layout";
import { TabbedTable } from "containers/TabbedTable";
import { trans } from "src/translations";

import { ManifestRemoveModal } from "../_shared";
import { getCreatePath } from "../utils";
import { ProviderManifestRow } from "./components/ProviderManifestRow";
import { ITEMS_PER_PAGE, TABS_CONFIG } from "./constants";
import styles from "./ManifestList.scss";
import { schema } from "./utils";

export const ManifestsListRend = ({
  manifestsByProvider = [],
  apps = [],
  loading,
  count,
  error,
  onSearch,
  availableFilters,
  fetchSuggestions,
  isAppIntegrator,
  defaultOrder,
  onRefetch,
  toggleButton = null,
}) => {
  const [selectedId, setSelectedId] = useState();
  const renderTableBody = (manifestsByProvider) => {
    return (
      <TableBody>
        {manifestsByProvider.map((provider) => (
          <ProviderManifestRow
            apps={apps}
            key={provider.providerId}
            provider={provider}
            setSelectedId={setSelectedId}
            isAppIntegrator={isAppIntegrator}
          />
        ))}
      </TableBody>
    );
  };

  return (
    <>
      <SearchBarWithFilters
        onSearch={onSearch}
        placeholder={trans.APP_MANIFESTS_SEARCH_PLACEHOLDER()}
        filtersDataChoices={availableFilters}
        validator={(input) => schema.validateSync(input)}
        suggestionApiEndpoint={fetchSuggestions}
      />
      {toggleButton}
      <ButtonsWrapper>
        <Button
          className={styles.button}
          type="green"
          to={getCreatePath()}
          dataTestId="create-manifest-button"
          disabled={!isAppIntegrator}
        >
          {trans.APP_MANIFESTS_CREATE_MANIFEST()}
        </Button>
      </ButtonsWrapper>
      <TabbedTable
        data={{
          loading,
          results: manifestsByProvider,
          count,
          error,
          defaultOrder,
        }}
        noResultsMessage={trans.APP_MANIFESTS_NO_RESULTS()}
        renderTableBody={renderTableBody}
        tabsConfig={TABS_CONFIG}
        rowsPerPage={ITEMS_PER_PAGE}
      />
      <ManifestRemoveModal
        id={selectedId}
        isOpen={Boolean(selectedId)}
        onClose={() => setSelectedId(undefined)}
        onRefetch={onRefetch}
      />
    </>
  );
};

ManifestsListRend.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onRefetch: PropTypes.func.isRequired,
  manifestsByProvider: PropTypes.arrayOf(
    PropTypes.shape({
      providerId: PropTypes.string.isRequired,
      manifests: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          created_date: PropTypes.string.isRequired,
          is_deleted: PropTypes.bool.isRequired,
          modified_date: PropTypes.string.isRequired,
          targets: PropTypes.object.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  apps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      icon: PropTypes.string,
      path: PropTypes.string,
    })
  ).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  availableFilters: PropTypes.shape({
    sections: PropTypes.arrayOf(filterSectionPropTypes),
  }),
  fetchSuggestions: PropTypes.func,
  isAppIntegrator: PropTypes.bool.isRequired,
  defaultOrder: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }),
  toggleButton: PropTypes.node,
};
